import { useEffect } from 'react'
import { CONTACT_CONFIG, CONTACT_DETAILS } from '../config/constants.js'
import { useLocale } from '../hooks/i18n/useLocale.js'
import { usePageMeta } from '../hooks/ui/usePageMeta.js'
import { WorkshopPrepSection } from './landing/WorkshopPrepSection.js'
import { LocaleToggle } from './ui/LocaleToggle.js'
import * as TOKENS from './chat/design-tokens'
import {
  AGENCY_PATH,
  BOOKING_LABEL,
  CHAT_LABEL,
  COPY_LABEL,
  OG_IMAGE,
  PAGE_TITLE,
  WORKSHOPS_PAGE,
  WORKSHOPS_URL,
  buildWorkshopsJsonLd,
  packagesFor,
  workshopFaqs,
  type CopyLang,
} from '../data/workshop-packages.js'

const shell = `min-h-[100dvh] bg-background text-foreground`
const wrap = 'mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 py-16 md:px-12'
const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`
const h1 = `text-4xl font-semibold tracking-tighter md:text-6xl ${TOKENS.TEXT.primary}`
const h2 = `text-2xl font-semibold tracking-tight md:text-3xl ${TOKENS.TEXT.primary}`
const body = `max-w-2xl text-base font-light leading-relaxed ${TOKENS.TEXT.secondary}`
// Base CTA box. Text colour is set per variant: one colour utility each, so the primary CTA
// never gets text-foreground on bg-foreground (invisible text).
const ctaBase = `inline-flex items-center justify-center border px-6 py-3 text-sm font-medium ${TOKENS.BORDER.default}`
const linkClass = `${ctaBase} ${TOKENS.TEXT.primary} hover:bg-muted`
const primaryCtaClass = `${ctaBase} bg-foreground text-background hover:bg-foreground/90`

function WorkshopsPage() {
  const { locale } = useLocale()
  const lang: CopyLang = locale === 'nb' ? 'nb' : 'en'
  const packages = packagesFor('hub', lang)
  const faqs = workshopFaqs(lang)
  const copy = WORKSHOPS_PAGE[lang]
  const [titleMain, titleSub] = copy.title.split('\n')
  const phone = CONTACT_DETAILS.find(item => item.label === 'Phone')
  const booking = CONTACT_CONFIG.SCHEDULING.BOOKING_URL

  usePageMeta(PAGE_TITLE.workshops[lang], `${copy.lead} ${copy.helpQuestion} ${copy.helpLink}`, WORKSHOPS_URL, {
    ogTitle: PAGE_TITLE.workshops[lang],
    ogImage: OG_IMAGE,
    ogUrl: WORKSHOPS_URL,
  })

  useEffect(() => {
    const existing = document.getElementById('workshops-structured-data')
    if (existing) existing.remove()
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'workshops-structured-data'
    script.textContent = JSON.stringify(buildWorkshopsJsonLd(lang))
    document.head.appendChild(script)
    return () => {
      script.remove()
    }
  }, [lang])

  return (
    <div className={shell}>
      <header className={`border-b ${TOKENS.BORDER.default}`}>
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-6 py-4 md:px-12">
          <a href="/" className={`font-mono text-lg font-semibold ${TOKENS.TEXT.primary}`}>
            F.B<span className={TOKENS.BRAND.slash}>/c</span>
          </a>
          <nav className="flex items-center gap-4" aria-label={copy.navLabel}>
            <a href={AGENCY_PATH} className={`text-sm ${TOKENS.TEXT.secondary}`}>
              {copy.agencyLink}
            </a>
            <LocaleToggle className="future-locale-toggle" />
          </nav>
        </div>
      </header>
      <main className={wrap}>
        <header className="flex flex-col gap-4">
          <span className={eyebrow}>F.B/c Lab</span>
          <h1 className={h1}>
            {titleMain}
            {titleSub ? (
              <>
                <br />
                <span className={TOKENS.TEXT.muted}>{titleSub}</span>
              </>
            ) : null}
          </h1>
          <p className={body}>
            {copy.lead} {copy.helpQuestion}{' '}
            <a href={AGENCY_PATH} className="underline">
              {copy.helpLink}
            </a>
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href="/chat" className={primaryCtaClass}>
              {CHAT_LABEL[lang]}
            </a>
            <a href={booking} className={linkClass} target="_blank" rel="noopener noreferrer">
              {BOOKING_LABEL[lang]}
            </a>
            {phone?.href ? (
              <a href={phone.href} className={linkClass}>
                {phone.value}
              </a>
            ) : null}
          </div>
        </header>

        <WorkshopPrepSection surface="hub" />

        {packages.map(item => (
          <section key={item.id} id={item.anchor} aria-labelledby={`${item.anchor}-title`} className={`border-t pt-10 ${TOKENS.BORDER.default}`}>
            <div className="flex flex-col gap-4 md:flex-row md:gap-12">
              <div className="flex w-full shrink-0 flex-col gap-3 md:w-56">
                <span className={eyebrow}>{item.format}</span>
                <span className={eyebrow}>{item.audience}</span>
              </div>
              <div className="flex flex-1 flex-col gap-4">
                <h2 id={`${item.anchor}-title`} className={h2}>
                  {item.name}
                </h2>
                <p className={body}>{item.summary}</p>
                <h3 className={eyebrow}>{COPY_LABEL.modules[lang]}</h3>
                <ul className="flex max-w-2xl flex-col gap-3">
                  {item.modules.map(mod => (
                    <li key={mod.id} className="flex flex-col gap-1">
                      <span className={`font-mono text-xs ${TOKENS.TEXT.primary}`}>
                        {mod.name}
                        {mod.note ? ` (${mod.note})` : ''}
                      </span>
                      <span className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>{mod.content}</span>
                    </li>
                  ))}
                </ul>
                <h3 className={eyebrow}>{COPY_LABEL.gets[lang]}</h3>
                <ul className="flex flex-wrap gap-x-6 gap-y-2">
                  {item.points.map(point => (
                    <li key={point} className={`inline-flex items-center gap-2 text-xs font-mono ${TOKENS.TEXT.muted}`}>
                      <span className={`h-1 w-1 shrink-0 rounded-full ${TOKENS.BRAND.accentBg}`} aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
                <p className={body}>{item.notIncluded}</p>
                {item.next ? <p className={body}>{item.next}</p> : null}
              </div>
            </div>
          </section>
        ))}

        <section id="faq" aria-labelledby="workshops-faq" className={`border-t pt-10 ${TOKENS.BORDER.default}`}>
          <h2 id="workshops-faq" className={h2}>
            {copy.faqTitle}
          </h2>
          <div className="mt-6 flex flex-col">
            {faqs.map(item => (
              <details key={item.question} className={`border-b py-4 ${TOKENS.BORDER.default}`}>
                <summary className={`cursor-pointer text-base font-medium ${TOKENS.TEXT.primary}`}>{item.question}</summary>
                <p className={`pt-3 ${body}`}>
                  {item.link && item.answer.startsWith(item.link.label) ? (
                    <>
                      <a href={item.link.href} className="underline" target="_blank" rel="noopener noreferrer">
                        {item.link.label}
                      </a>
                      {item.answer.slice(item.link.label.length)}
                    </>
                  ) : (
                    item.answer
                  )}
                </p>
              </details>
            ))}
          </div>
        </section>

        <p className={body}>
          <a href="/" className="underline">
            F.B/c Lab
          </a>
          {' · '}
          <a href={AGENCY_PATH} className="underline">
            {copy.agencyLink}
          </a>
        </p>
      </main>
    </div>
  )
}

export default WorkshopsPage
